<template>
  <div class="bg-white min-h-[60vh]">

    <!-- Onbekend topic -->
    <section v-if="!topic" class="max-w-screen-xl mx-auto px-6 py-20 text-center">
      <h1 class="text-[1.75rem] font-black text-[#111] mb-3">Keuzehulp niet gevonden</h1>
      <p class="text-[#666] mb-6">Kies hieronder een keuzehulp om te starten.</p>
      <router-link to="/keuzehulp" class="btn-dark">Naar alle keuzehulpen</router-link>
    </section>

    <template v-else>
      <!-- Hero -->
      <section class="bg-[#111] text-white">
        <div class="max-w-screen-xl mx-auto px-6 py-10 lg:py-12">
          <router-link to="/keuzehulp" class="inline-flex items-center gap-2 text-[#aaa] hover:text-white text-[0.85rem] no-underline mb-4">
            <i class="bi bi-arrow-left" aria-hidden="true"></i> Alle keuzehulpen
          </router-link>
          <h1 class="text-white text-[clamp(1.5rem,4vw,2.5rem)] font-black leading-[1.1] flex items-center gap-3">
            <i :class="['bi text-[#FFD500]', topic.icon]" aria-hidden="true"></i> {{ topic.title }}
          </h1>
        </div>
      </section>

      <div class="max-w-[720px] mx-auto px-6 py-10 lg:py-14">

        <!-- ── Vragen ──────────────────────────────────────── -->
        <div v-if="!done">
          <!-- Voortgang -->
          <div class="mb-6">
            <div class="flex justify-between text-[0.8rem] text-[#888] mb-2">
              <span>Vraag {{ step + 1 }} van {{ topic.questions.length }}</span>
              <span>{{ Math.round(((step) / topic.questions.length) * 100) }}%</span>
            </div>
            <div class="h-2 rounded-full bg-[#eee] overflow-hidden">
              <div class="h-full bg-[#FFD500]" style="transition: width 280ms cubic-bezier(0.23,1,0.32,1)" :style="{ width: `${(step / topic.questions.length) * 100}%` }"></div>
            </div>
          </div>

          <transition name="kh-swap" mode="out-in">
            <div :key="step">
              <h2 class="text-[clamp(1.25rem,3vw,1.75rem)] font-black text-[#111] mb-6">
                {{ topic.questions[step].q }}
              </h2>

              <div class="flex flex-col gap-3">
                <button
                  v-for="(opt, i) in topic.questions[step].options"
                  :key="i"
                  type="button"
                  @click="choose(i)"
                  class="group flex items-center gap-4 w-full text-left p-4 sm:p-5 rounded-[14px] border-2 border-[#e9eaec] bg-white hover:border-[#FFD500] hover:bg-[#fffdf0] transition-[border-color,background-color] duration-150"
                >
                  <span class="w-11 h-11 rounded-[10px] bg-[#f5f5f5] group-hover:bg-[#FFD500] text-[#111] flex items-center justify-center shrink-0 transition-colors">
                    <i :class="['bi text-[1.2rem]', opt.icon || 'bi-dot']" aria-hidden="true"></i>
                  </span>
                  <span class="font-semibold text-[1rem] text-[#111]">{{ opt.label }}</span>
                  <i class="bi bi-arrow-right text-[#ccc] group-hover:text-[#FFD500] ml-auto transition-colors" aria-hidden="true"></i>
                </button>
              </div>
            </div>
          </transition>

          <button v-if="step > 0" type="button" @click="back" class="mt-6 text-[0.9rem] text-[#666] hover:text-[#111] inline-flex items-center gap-1.5">
            <i class="bi bi-chevron-left" aria-hidden="true"></i> Vorige vraag
          </button>
        </div>

        <!-- ── Resultaat ───────────────────────────────────── -->
        <div v-else>
          <div class="text-center mb-8">
            <span class="inline-flex items-center gap-2 text-[0.8rem] font-bold uppercase tracking-[0.06em] text-[#b58a00] bg-[#fffbe6] px-3 py-1 rounded-full mb-4">
              <i class="bi bi-check-circle-fill" aria-hidden="true"></i> Onze aanbeveling
            </span>
            <h2 class="text-[clamp(1.5rem,4vw,2.25rem)] font-black text-[#111] mb-2">{{ result.name }}</h2>
            <p class="text-[1.0625rem] text-[#555] leading-[1.6] max-w-[520px] mx-auto">{{ result.sub }}</p>
          </div>

          <!-- Product-preview -->
          <div v-if="loadingProducts" class="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <div v-for="n in 4" :key="n" class="rounded-[12px] bg-[#f4f4f4] aspect-[3/4] animate-pulse"></div>
          </div>
          <div v-else-if="products.length" class="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <router-link
              v-for="p in products" :key="p.id"
              :to="`/product/${p.slug}`"
              class="group rounded-[12px] border border-[#e9eaec] bg-white overflow-hidden no-underline hover:border-[#FFD500] transition-colors"
            >
              <div class="aspect-square bg-white overflow-hidden">
                <img v-if="p.images && p.images.length"
                     :src="sized(p.images[0].src, 300)" :alt="p.name"
                     width="300" height="300" loading="lazy" decoding="async"
                     class="w-full h-full object-contain p-2 group-hover:scale-[1.04] transition-transform" />
              </div>
              <div class="p-3">
                <p class="text-[0.8rem] font-semibold text-[#111] leading-tight line-clamp-2 mb-1">{{ p.name }}</p>
                <p class="text-[0.9rem] font-black text-[#111] m-0">{{ formatPrice(p.price) }}</p>
              </div>
            </router-link>
          </div>

          <div class="flex flex-col sm:flex-row gap-3">
            <router-link :to="`/categorie/${resultSlug}`" class="btn-primary flex-1 justify-center">
              <i class="bi bi-grid" aria-hidden="true"></i> Bekijk alle {{ result.name.toLowerCase() }}
            </router-link>
            <button type="button" @click="restart" class="btn-ghost justify-center">
              <i class="bi bi-arrow-repeat" aria-hidden="true"></i> Opnieuw
            </button>
          </div>

          <p class="text-center text-[0.9rem] text-[#888] mt-8">
            Twijfel je nog?
            <router-link to="/gratis-energieadvies" class="text-[#b58a00] font-semibold no-underline hover:underline">Vraag gratis persoonlijk advies aan</router-link>.
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { formatPrice } from '@/utils/price'
import { getKeuzehulpTopic } from '@/config/keuzehulp'
import { cachedFetch } from '@/composables/useApiCache.js'

const route = useRoute()
const topic = computed(() => getKeuzehulpTopic(route.params.topic))

// Aanbevolen producten per resultaat-slug ophalen mét client-cache (10 min).
// Categorie-lookup 30 min (verandert zelden). Zo is een herhaalde uitkomst instant.
const productCache = new Map()
async function loadProductsFor(slug) {
  if (!slug) return []
  if (productCache.has(slug)) return productCache.get(slug)
  const p = (async () => {
    try {
      const cats = await cachedFetch(`/_wc/v3/products/categories?slug=${encodeURIComponent(slug)}`, 30 * 60 * 1000)
      const catId = cats?.[0]?.id
      if (!catId) return []
      return await cachedFetch(
        `/_wc/v3/products?category=${catId}&per_page=4&status=publish&orderby=popularity&_fields=id,name,slug,price,images`,
        10 * 60 * 1000
      ) || []
    } catch { return [] }
  })()
  productCache.set(slug, p)
  return p
}

// Zodra de keuzehulp opent: warm álle mogelijke uitkomsten alvast op de
// achtergrond, zodat de aanbeveling meteen klaarstaat als de quiz af is.
function warmResults() {
  const results = topic.value?.results
  if (!results) return
  Object.keys(results).forEach(slug => { loadProductsFor(slug) })
}
onMounted(warmResults)

const step = ref(0)
const answers = ref([])            // gekozen optie-index per vraag
const done = ref(false)
const resultSlug = ref('')
const result = ref({ name: '', sub: '' })
const products = ref([])
const loadingProducts = ref(false)

const sized = (url, w) => {
  if (!url) return url
  const i = url.indexOf('/wp-content/')
  return i === -1 ? url : `${url.slice(i)}?w=${w}`
}

function choose(optIdx) {
  answers.value[step.value] = optIdx
  if (step.value < topic.value.questions.length - 1) {
    step.value++
  } else {
    finish()
  }
}

function back() {
  if (step.value > 0) step.value--
}

function restart() {
  step.value = 0
  answers.value = []
  done.value = false
  products.value = []
}

async function finish() {
  // Punten optellen per categorie-slug
  const scores = {}
  topic.value.questions.forEach((q, qi) => {
    const opt = q.options[answers.value[qi]]
    if (!opt) return
    for (const [slug, pts] of Object.entries(opt.points || {})) {
      scores[slug] = (scores[slug] || 0) + pts
    }
  })
  const winner = Object.entries(scores).sort((a, b) => b[1] - a[1])[0]?.[0]
             || Object.keys(topic.value.results)[0]

  resultSlug.value = winner
  result.value = topic.value.results[winner] || { name: winner, sub: '' }
  done.value = true

  // Passende producten tonen — meestal al gewarmd, dus direct uit de cache.
  loadingProducts.value = true
  products.value = []
  try {
    products.value = await loadProductsFor(winner)
  } catch (e) {
    console.error('[keuzehulp] producten laden mislukt:', e.message)
  } finally {
    loadingProducts.value = false
  }
}

// Bij wisselen van topic: reset + nieuwe uitkomsten alvast warmen
watch(() => route.params.topic, () => { restart(); warmResults() })
</script>

<style scoped>
/* Vloeiende wissel tussen quizvragen (fade + kleine verschuiving). */
.kh-swap-enter-active { transition: opacity .22s ease, transform .22s ease; }
.kh-swap-leave-active { transition: opacity .12s ease, transform .12s ease; }
.kh-swap-enter-from { opacity: 0; transform: translateY(8px); }
.kh-swap-leave-to   { opacity: 0; transform: translateY(-8px); }
@media (prefers-reduced-motion: reduce) {
  .kh-swap-enter-active, .kh-swap-leave-active { transition: none; }
  .kh-swap-enter-from, .kh-swap-leave-to { transform: none; }
}

.btn-primary, .btn-dark {
  display: inline-flex; align-items: center; gap: .5rem;
  background: #FFD500; color: #111; font-weight: 700; font-size: .95rem;
  padding: .85rem 1.5rem; border-radius: 10px; border: none; cursor: pointer;
  text-decoration: none; transition: background .15s;
}
.btn-primary:hover, .btn-dark:hover { background: #ffb800; }
.btn-dark { background: #111; color: #fff; }
.btn-dark:hover { background: #333; }
.btn-ghost {
  display: inline-flex; align-items: center; gap: .5rem;
  background: #f5f6f7; color: #333; font-weight: 600; font-size: .9rem;
  padding: .85rem 1.5rem; border-radius: 10px; border: none; cursor: pointer;
  text-decoration: none; transition: background .15s;
}
.btn-ghost:hover { background: #ececee; }
.line-clamp-2 {
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
</style>
